"use client";

import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Home, ArrowLeft, RefreshCw, type LucideIcon } from "lucide-react";

interface ErrorPageLayoutProps {
  code: string;
  title: string;
  description: string;
  icon: LucideIcon;
  showRetry?: boolean;
  onRetry?: () => void;
  showGoBack?: boolean;
  additionalActions?: React.ReactNode;
}

export function ErrorPageLayout({
  code,
  title,
  description,
  icon: Icon,
  showRetry = false,
  onRetry,
  showGoBack = true,
  additionalActions,
}: ErrorPageLayoutProps) {
  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="max-w-lg w-full p-8 md:p-12 text-center">
        {/* Error Code */}
        <div className="mb-6">
          <span className="text-8xl md:text-9xl font-bold text-accent/20">
            {code}
          </span>
        </div>

        {/* Icon */}
        <div className="mb-6 flex justify-center">
          <div className="p-4 rounded-2xl bg-muted border border-border">
            <Icon className="w-12 h-12 text-muted-foreground" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-semibold text-foreground mb-4">
          {title}
        </h1>

        {/* Description */}
        <p className="text-muted-foreground text-base md:text-lg mb-8 leading-relaxed">
          {description}
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {showGoBack && (
            <Button
              variant="outline"
              size="lg"
              onClick={() => window.history.back()}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Go Back
            </Button>
          )}

          {showRetry && onRetry && (
            <Button
              variant="outline"
              size="lg"
              onClick={onRetry}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </Button>
          )}

          <Button asChild size="lg" className="gap-2">
            <Link to="/">
              <Home className="w-4 h-4" />
              Back to Home
            </Link>
          </Button>

          {additionalActions}
        </div>
      </Card>
    </div>
  );
}
